import { Fragment, Node } from 'prosemirror-model';
import { EditorState, Transaction } from 'prosemirror-state';

import { extractParagraphsFromBlockquote, getSelectedParagraphs } from './paragraphUtils';
import { findAdjacentBlockquotes } from './rangeUtils';

/**
 * マージされた段落配列と置換範囲を計算する
 */
export function calculateMergeData(
  state: EditorState,
  adjacentBlockquotes: {
    before: { node: Node; pos: number } | null;
    after: { node: Node; pos: number } | null;
  },
  selectedParagraphs: Node[],
): { mergedParagraphs: Node[]; replaceStart: number; replaceEnd: number } {
  const { schema, selection } = state;
  const { from, to } = selection;
  const $from = state.doc.resolve(from);
  const $to = state.doc.resolve(to);

  let replaceStart = $from.before($from.depth);
  let replaceEnd = $to.after($to.depth);
  const mergedParagraphs: Node[] = [];

  // 前のblockquoteの段落を追加
  if (adjacentBlockquotes.before) {
    replaceStart = adjacentBlockquotes.before.pos;
    const beforeParagraphs = extractParagraphsFromBlockquote(
      adjacentBlockquotes.before.node,
      schema,
    );
    mergedParagraphs.push(...beforeParagraphs);
  }

  // 選択された段落を追加
  mergedParagraphs.push(...selectedParagraphs);

  // 後のblockquoteの段落を追加
  if (adjacentBlockquotes.after) {
    replaceEnd = adjacentBlockquotes.after.pos + adjacentBlockquotes.after.node.nodeSize;
    const afterParagraphs = extractParagraphsFromBlockquote(adjacentBlockquotes.after.node, schema);
    mergedParagraphs.push(...afterParagraphs);
  }

  return { mergedParagraphs, replaceStart, replaceEnd };
}

/**
 * 新しいblockquoteを隣接するblockquoteとマージして適用する
 */
export function applyBlockquoteWithMerging(
  state: EditorState,
  dispatch?: (tr: Transaction) => void,
  trArg?: Transaction,
): boolean {
  const { schema } = state;
  const { from, to } = state.selection;
  let transaction = trArg || state.tr;

  // 隣接blockquoteがない場合は何もしない
  const adjacentBlockquotes = findAdjacentBlockquotes(state, from, to);
  if (!adjacentBlockquotes.before && !adjacentBlockquotes.after) {
    return false;
  }

  // 選択範囲内の段落を取得
  const selectedParagraphs = getSelectedParagraphs(state, from, to);
  if (selectedParagraphs.length === 0) {
    return false;
  }

  // マージデータを計算
  const { mergedParagraphs, replaceStart, replaceEnd } = calculateMergeData(
    state,
    adjacentBlockquotes,
    selectedParagraphs,
  );

  // 新しいblockquoteを作成して置換
  const newBlockquote = schema.nodes.blockquote.create(null, Fragment.from(mergedParagraphs));
  transaction = transaction.replaceWith(replaceStart, replaceEnd, newBlockquote);

  if (dispatch) {
    dispatch(transaction);
  }

  return true;
}
